import React, { useState } from 'react'
import { Card, CARD_THEME, CheckBox, Select } from '@st-operations/uikit/src/v2/index.js'
import format from '@st-operations/uikit/lib/format.js'

import WordCloud from '../../word-cloud/index.jsx'

import { useTopKecc } from './utils.js'
import OnClickOutside from '_components/OnClickOutside.jsx'
import SettingsToggle from '_components/settings-toggle/index.jsx'
import ButtonSelector from '_components/button-selector'
import { useModal } from '_hooks'
import KeccTopDomainsModal from './kecc-top-domains-modal/index.jsx'

import {
  VISUALIZATION_TYPES,
  TYPES,
  PERIODS,
  ENTITY_TYPES,
  FILTERS,
  MAP_TYPES_TO_COLORS,
} from './utils.js'
import capitalizeFirstLetter from '_lib/captalizeFirstLetter.js'

import './styles.scss'
import { useLazyLoad } from '_hooks'

const KeywordsCloud = () => {
  const [form, setForm] = useState({
    field: 'keywords',
    lookback: 'yesterday',
    entity_type: '',
    language: 'en',
  })
  const { trendingTerms, filter, isLoading, setFilter } = useTopKecc(form)
  const [visualization, setVisualization] = useState(VISUALIZATION_TYPES.WORD_CLOUD)
  const [top, setTop] = useState(15)
  const topOptions = [5, 10, 15, 20, 25, 30]
  const {
    isOpen: isTopDomainsModalOpen,
    handleCloseModal: handleTopDomainsModalClose,
    handleOpenModal: handleTopDomainsModalOpen,
  } = useModal()
  const [clickedWord, setClickedWord] = useState()

  const words = trendingTerms[filter] || []

  const handleChangeSelect = (value, name) => {
    setForm((prev) => ({ ...prev, [name]: value.value }))
  }

  const handleChangeFilter = (value) => {
    setFilter(value.value)
  }

  const handleChangeCheckbox = () => {
    setForm((prev) => ({ ...prev, language: form.language === 'en' ? 'all' : 'en' }))
  }

  return (
    <section className="scale-and-coverage__container">
      <div className="data-preview-highlights">
        <Card className="kecc__container" theme={CARD_THEME.NO_SHADOW}>
          <div className="kecc__header">
            <h2>Counts</h2>
            <div className="kecc__label-header">
              <SettingsToggle
                label={`${FILTERS.find((f) => f.value === filter).label || ''} ${
                  TYPES.find((type) => type.value === form.field)?.label?.toLowerCase() || ''
                } since ${
                  PERIODS.find((period) => period.value === form.lookback).label?.toLowerCase() ||
                  ''
                }`}
              >
                {({ onClose }) => (
                  <Card className="kecc__config-dialog">
                    <OnClickOutside onClickOutside={() => onClose()}>
                      <Select
                        label="Visualization Type"
                        options={[
                          { label: 'Word Cloud', value: VISUALIZATION_TYPES.WORD_CLOUD },
                          { label: 'Bar Chart', value: VISUALIZATION_TYPES.BAR_CHART },
                        ]}
                        onChange={(value) => {
                          setVisualization(value.value)
                        }}
                        name="visualization"
                        value={visualization}
                        useReactSelect
                      />
                      <If condition={visualization === VISUALIZATION_TYPES.BAR_CHART}>
                        <Select
                          label="Top"
                          options={topOptions.map((option) => ({ label: option, value: option }))}
                          onChange={(value) => {
                            setTop(value.value)
                          }}
                          name="top"
                          value={top}
                          useReactSelect
                        />
                      </If>
                      <Select
                        label="Period"
                        options={PERIODS}
                        onChange={handleChangeSelect}
                        name="lookback"
                        value={form.lookback}
                        useReactSelect
                      />
                      <If condition={form.field === 'entities'}>
                        <Select
                          label="Entity Type"
                          options={ENTITY_TYPES}
                          onChange={handleChangeSelect}
                          name="entityType"
                          value={form.entityType}
                          useReactSelect
                        />
                      </If>
                      <Select
                        label="Filter"
                        options={FILTERS}
                        onChange={handleChangeFilter}
                        value={filter}
                        useReactSelect
                      />
                      <CheckBox
                        label={
                          <span
                            style={{
                              ...(form.language === 'en'
                                ? { color: '#13682b', fontWeight: 600 }
                                : {}),
                            }}
                          >
                            Only show terms from English language websites
                          </span>
                        }
                        name="language"
                        onChange={handleChangeCheckbox}
                        checked={form.language === 'en'}
                      />
                    </OnClickOutside>
                  </Card>
                )}
              </SettingsToggle>
            </div>
          </div>
          <div className="kecc__content">
            <div className="kecc__content-inner">
              <ButtonSelector
                options={TYPES}
                value={form.field}
                onChange={(_, value) => {
                  setForm((form) => ({ ...form, field: value }))
                }}
              />
              <If condition={visualization === 'wordCloud'}>
                <WordCloud
                  data={words}
                  isLoading={isLoading}
                  onWordClick={(word) => {
                    setClickedWord(word.text)
                    handleTopDomainsModalOpen()
                  }}
                />
              </If>
              <If condition={visualization === VISUALIZATION_TYPES.BAR_CHART}>
                <HorizontalBarChart
                  data={words.sort((a, b) => b.size - a.size).slice(0, top)}
                  field={form.field}
                  onBarClick={({ text }) => {
                    setClickedWord(text)
                    handleTopDomainsModalOpen()
                  }}
                />
              </If>
              <KeccTopDomainsModal
                field={form.field}
                word={clickedWord}
                isOpen={isTopDomainsModalOpen}
                onClose={handleTopDomainsModalClose}
              />
            </div>
          </div>
        </Card>
      </div>
    </section>
  )
}

const HorizontalBarChart = React.memo(
  ({ data, field, onBarClick }) => {
    const [
      { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis },
      loadingRecharts,
    ] = useLazyLoad(import('recharts'))

    return (
      <If condition={!loadingRecharts}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <YAxis
              type="category"
              dataKey="text"
              tickLine={false}
              tickSize="0px"
              label={{
                value: capitalizeFirstLetter(field),
                angle: -90,
                position: 'insideLeft',
                offset: 20,
                style: { textAnchor: 'middle' },
              }}
              width={40}
            />
            <XAxis
              type="number"
              dataKey="size"
              tickFormatter={(value) => {
                return format('metric', value)
              }}
              domain={[0, (dataMax) => dataMax * 1.2]}
              label={{
                value: 'Event count',
                position: 'bottom',
                offset: -20,
                style: { textAnchor: 'middle' },
              }}
              height={50}
            />
            <Tooltip />
            <Bar
              dataKey="size"
              fill={MAP_TYPES_TO_COLORS[field]}
              className="kecc__bar-chart-bar"
              onClick={onBarClick}
            >
              <LabelList dataKey="text" position="right" fill="#666666" onClick={onBarClick} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </If>
    )
  },
  (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps)
)

export default KeywordsCloud
